/**
 * pj-variant-price-guard.js — PanamaJack
 * ---------------------------------------------------------------------------
 * FEATURE (regla de negocio PJ, iso-funcional con 6.0.0):
 *   Una variante con price == 0 (precio "sin definir") NO es comprable y su
 *   precio queda OCULTO. En cuanto se selecciona una variante con precio > 0,
 *   se restaura el boton de compra y los precios.
 *
 *   Origen 6.0.0 (current_template/assets/theme.js):
 *     - updateButtons : habilita add-to-cart solo si
 *         `variant && variant.available == true && variant.price != 0`.
 *     - updatePrice   : `if (variant.price == 0)` => ocultar `.was-price` y
 *         `.current-price`; else mostrarlos (display inline-block).
 *
 * STACK 10.2.0 (vendor, web components nativos):
 *   - El <variant-picker> dispara `on:variant:change` (bubbles) con
 *       evt.detail.variant  -> objeto variante Shopify (price en centimos),
 *       evt.detail.form      -> el <form> de producto.
 *   - En cada cambio el vendor RE-RENDERIZA por AJAX los nodos
 *     `[data-dynamic-variant-content]` (la `.price-area` y el boton
 *     `[name="add"]` del <product-form>) y fija el `disabled` del boton SOLO
 *     por disponibilidad (NO contempla price==0). Por eso este guard escucha
 *     `on:variant:change`, que se dispara DESPUES del re-render, y reaplica la
 *     regla PJ (override) sobre el markup ya actualizado.
 *
 * MARKUP objetivo (sections/main-product.liquid, 10.2.0):
 *   - Precio:   .price-area > (.was-price.theme-money | .price.theme-money | unit-price)
 *               (se cubre tambien `.current-price` legacy por robustez).
 *   - Compra:   <product-form> button[name="add"] (tipo submit).
 *   - Contenedor de scope: `.product-area` (sobre el que burbujea el evento).
 *
 * Implementacion: vanilla JS, null-guards, idempotente. No reimplementa fetch
 * ni el carrito AJAX nativo (<cart-form>); solo aplica/quita disabled + ocultar.
 *
 * FASE C: este modulo se cablea desde layout/theme.liquid con
 *   <script src="{{ 'pj-variant-price-guard.js' | asset_url }}" defer></script>
 * No toca theme.js ni el markup.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var PRODUCT_AREA_SELECTOR = '.product-area';
  var PRICE_AREA_SELECTOR = '.price-area';
  // Nodos de precio a ocultar/mostrar dentro de .price-area.
  // 10.2.0 usa .price; se incluye .current-price por compat. con el origen 6.0.0.
  var PRICE_NODE_SELECTORS = '.was-price, .price, .current-price, .unit-price';
  var ADD_BUTTON_SELECTOR = '[name="add"]';
  var DATA_DISABLED_ATTR = 'data-disabled';
  // Marca de estado para restaurar el display original sin reintroducir layout.
  var ORIG_DISPLAY_KEY = 'pjPriceGuardOrigDisplay';

  /**
   * Resuelve la variante "actual" de un .product-area en carga inicial,
   * cuando aun no se ha disparado ningun `on:variant:change`.
   * @param {Element} productArea
   * @returns {?object} variante Shopify o null si no se puede resolver.
   */
  function resolveCurrentVariant(productArea) {
    var idInput = productArea.querySelector('input[name="id"]');
    var dataEl = productArea.querySelector('variant-picker script[data-variant-data]');
    if (!idInput || !dataEl) return null;

    var currentId = parseInt(idInput.value, 10);
    if (!currentId) return null;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants)) return null;

    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && variants[i].id === currentId) return variants[i];
    }
    return null;
  }

  /**
   * Oculta los nodos de precio guardando su display original (idempotente).
   * @param {Element} priceArea
   */
  function hidePrices(priceArea) {
    var nodes = priceArea.querySelectorAll(PRICE_NODE_SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.dataset[ORIG_DISPLAY_KEY] === undefined) {
        node.dataset[ORIG_DISPLAY_KEY] = node.style.display || '';
      }
      node.style.display = 'none';
    }
  }

  /**
   * Restaura los nodos de precio a su display original (idempotente).
   * @param {Element} priceArea
   */
  function showPrices(priceArea) {
    var nodes = priceArea.querySelectorAll(PRICE_NODE_SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.dataset[ORIG_DISPLAY_KEY] !== undefined) {
        node.style.display = node.dataset[ORIG_DISPLAY_KEY];
        delete node.dataset[ORIG_DISPLAY_KEY];
      }
    }
  }

  /**
   * Aplica o revierte la regla PJ de precio 0 sobre un .product-area.
   * @param {Element} productArea
   * @param {?object} variant - evt.detail.variant (price en centimos).
   */
  function applyGuard(productArea, variant) {
    if (!productArea) return;

    // price==0 SOLO cuando hay variante y su price es exactamente 0.
    // (Sin variante / variante undefined => no es un caso "precio 0", lo
    // gestiona el vendor; no pisamos su estado.)
    var isZeroPrice = !!variant && variant.price === 0;

    var addButton = productArea.querySelector(ADD_BUTTON_SELECTOR);
    var priceArea = productArea.querySelector(PRICE_AREA_SELECTOR);

    if (isZeroPrice) {
      if (addButton) {
        addButton.disabled = true;
        addButton.setAttribute(DATA_DISABLED_ATTR, 'true');
      }
      if (priceArea) hidePrices(priceArea);
    } else {
      // Restaurar SOLO lo que este modulo pudo haber tocado. El disabled por
      // disponibilidad/no-variante lo gestiona el <product-form> del vendor:
      // unicamente retiramos nuestro propio marcador y el disabled si fuimos
      // nosotros quienes lo pusimos por precio 0.
      if (addButton && addButton.getAttribute(DATA_DISABLED_ATTR) === 'true') {
        addButton.removeAttribute(DATA_DISABLED_ATTR);
        // Solo re-habilitamos si la variante esta disponible; si no, el vendor
        // ya la habra dejado disabled por su cuenta (no la forzamos).
        if (variant && variant.available === true) {
          addButton.disabled = false;
        }
      }
      if (priceArea) showPrices(priceArea);
    }
  }

  /**
   * Listener bubbling de `on:variant:change`: aplica el guard sobre el
   * .product-area del evento usando evt.detail.variant del vendor 10.2.0.
   * @param {CustomEvent} evt
   */
  function onVariantChange(evt) {
    if (!evt || !evt.detail) return;
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;
    var productArea = target.closest(PRODUCT_AREA_SELECTOR);
    if (!productArea) return;
    applyGuard(productArea, evt.detail.variant);
  }

  /** Pasada inicial: resuelve la variante actual de cada ficha y aplica guard. */
  function initialPass() {
    var areas = document.querySelectorAll(PRODUCT_AREA_SELECTOR);
    for (var i = 0; i < areas.length; i++) {
      var variant = resolveCurrentVariant(areas[i]);
      if (variant) applyGuard(areas[i], variant);
    }
  }

  function init() {
    if (window.__pjVariantPriceGuardInit) return; // idempotente (anti doble-cableado)
    window.__pjVariantPriceGuardInit = true;

    // Bubbling a nivel documento: cubre fichas, quick-buy y secciones recargadas.
    document.addEventListener('on:variant:change', onVariantChange);
    initialPass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
